import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'

// @mui material components
import Card from '@mui/material/Card'
import Badge from '@mui/material/Badge'
import Modal from '@mui/material/Modal'

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'examples/Navbars/DashboardNavbar'
import Footer from 'examples/Footer'
import DataTable from 'examples/Tables/DataTable'
import MDAlert from 'components/MDAlert'
import { Tooltip, IconButton, CardContent, Typography } from '@mui/material'

import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'

import CrudService from 'services/cruds-service'
import { AbilityContext } from 'Can'
import { useAbility } from '@casl/react'
import { useNavigate } from 'react-router-dom'
import MDButton from 'components/MDButton'

function CampaignContactList() {
    let { state } = useLocation()
    const ability = useAbility(AbilityContext)
    const [data, setData] = useState([])
    const [tableData, setTableData] = useState([])
    const [notification, setNotification] = useState({
        value: false,
        text: '',
    })
    const [loader, setLoader] = useState(false)
    const navigate = useNavigate();

    useEffect(() => {
        ; (async () => {
            try {
                setLoader(true)
                const response = await CrudService.getAllCampaignContact()
                setData(response.data)
                setLoader(false)
            } catch(error){
                console.error('error', error)
                setLoader(false)
            }
        })()
        document.title = `Rivio | Campaign | Contact`
    }, [])

    useEffect(() => {
        if (!state) return
        setNotification({
            value: state.value,
            text: state.text,
        })
    }, [state])

    useEffect(() => {
        setTableData(getRows(data))
    }, [data])

    useEffect(() => {
        if (notification.value === true) {
            let timer = setTimeout(() => {
                setNotification({
                    value: false,
                    text: '',
                })
            }, 5000)
        }
    }, [notification])

    const clickAddHandler = () => {
        navigate('/campaign/contact/new')
    }

    const clickEditHandler = async (id) => {
        navigate(`/campaign/contact/edit/${id}`)
    }

    const clickDeleteHandler = async (e, id) => {
        try {
            if (!confirm('Are you sure you want to delete this Contact?')) {
                e.nativeEvent.stopImmediatePropagation()
            } else {
                setLoader(true)
                await CrudService.removeCampaignContact(id)
                // the delete does not send a response
                // so I need to get again the categories to set it and this way the table gets updated -> it goes to the useEffect with data dependecy
                const response = await CrudService.getAllCampaignContact()
                setData(response.data)
                setNotification({
                    value: true,
                    text: 'The Contact has been successfully deleted',
                })
                setLoader(false)
            }
        } catch (err) {
            // it sends error is the API is associated with an API
            console.error(err)
            if (err.hasOwnProperty('msg')) {
                setNotification({
                    value: true,
                    text: err.msg,
                })
            }
            setLoader(false)
            return null
        }
    }

    const getRows = (info) => {
        let updatedInfo = info.map((row) => {
            let color = row?.status_type_id === 1 ? 'success' : 'error'
            let variant = 'contained'
            let badgeContent = row?.StatusType?.status_name

            return {
                id: row.contact_id,
                company_id: row.company_id,
                company_name: row?.CampaignCompany?.company_name,
                contact_type: row?.CampaignContactType?.type_name,
                batch: row?.CampaignContactsBatch?.batch_name,
                company: row?.CampaignCompany?.company_name,
                first_name: row.first_name,
                last_name: row.last_name,
                email: row.email,
                phone: row.phone,
                confidence: row.confidence,
                department: row.department,
                linkedin: row.linkedin,
                position: row.position,
                status: (
                    <Badge
                        color={color}
                        variant={variant}
                        badgeContent={badgeContent}
                        style={{
                            display: 'inline-block',
                            whiteSpace: 'nowrap',
                            marginLeft: '20px',
                        }}
                    ></Badge>
                )
            }
        })
        return updatedInfo
    }

    const dataTableData = {
        columns: [
            { Header: 'ID', accessor: 'id' },
            { Header: 'Company ID', accessor: 'company_id' },
            { Header: 'Company Name', accessor: 'company_name' },
            { Header: 'Type', accessor: 'contact_type' },
            { Header: 'batch', accessor: 'batch' },
            { Header: 'position', accessor: 'position' },
            { Header: 'department', accessor: 'department' },
            { Header: 'company', accessor: 'company' },
            { Header: 'first name', accessor: 'first_name' },
            { Header: 'last name', accessor: 'last_name' },
            { Header: 'email', accessor: 'email' },
            { Header: 'phone', accessor: 'phone' },
            { Header: 'linkedin', accessor: 'linkedin' },
            { Header: 'confidence', accessor: 'confidence' },
            { Header: 'status', accessor: 'status' },
            {
                Header: 'actions',
                disableSortBy: true,
                accessor: '',
                Cell: (info) => {
                    return (
                        <MDBox display="flex" alignItems="center">
                            {ability.can('edit', 'campaign') && (
                                <Tooltip title="Edit Campaign">
                                    <IconButton
                                        onClick={() =>
                                            clickEditHandler(info.cell.row.original.id)
                                        }
                                    >
                                        <MDTypography>
                                            <EditIcon />
                                        </MDTypography>
                                    </IconButton>
                                </Tooltip>
                            )}

                            {ability.can('delete', 'campaign') && (
                                <Tooltip title="Delete Campaign">
                                    <IconButton
                                        onClick={(e) =>
                                            clickDeleteHandler(e, info.cell.row.original.id)
                                        }
                                    >
                                        <MDTypography>
                                            <DeleteIcon />
                                        </MDTypography>
                                    </IconButton>
                                </Tooltip>
                            )}
                        </MDBox>
                    )
                },
            },
        ],

        rows: tableData,
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            {notification.value && (
                <MDAlert color="info" my="20px">
                    <MDTypography variant="body2" color="white">
                        {notification.text || ''}
                    </MDTypography>
                </MDAlert>
            )}
            <MDBox pt={1}>
                <MDBox >
                    <Card>
                        <MDBox
                            p={3}
                            lineHeight={1}
                            display="flex"
                            justifyContent="space-between"
                        >
                            <MDTypography variant="h5" fontWeight="medium">
                                Campaign Contact
                            </MDTypography>
                            {ability.can('create', 'campaign') && (
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    size="small"
                                    type="submit"
                                    onClick={clickAddHandler}
                                >
                                    + Add Contact
                                </MDButton>
                            )}
                        </MDBox>

                        <DataTable table={dataTableData} canSearch={true} tableHeight="calc(100vh - 220px)" loader={loader}/>
                        {/* <MultiSelectDataTable rowData={getRows(data)} selectedRows={selectedRows} setSelectedRows={setSelectedRows}/> */}
                    </Card>
                </MDBox>
            </MDBox>
            <Footer />
        </DashboardLayout>
    )
}

export default CampaignContactList